import liveDropRaw from './liveDrop.generated.json';

export type BenchmarkMatchType = 'exact_or_close' | 'brand_family' | 'category_proxy' | 'category_proxy_prorated' | 'category_proxy_per_ball';

export type MarketBenchmark = {
  sourceId: string;
  competitor: string;
  evidenceUrl: string;
  benchmarkProduct: string;
  competitorPriceInclVat: number;
  matchType: BenchmarkMatchType;
};

export type LiveDropProduct = {
  sku: string;
  slug: string;
  name: string;
  category: string;
  brand: string;
  audience: string;
  level: string;
  priority: string;
  commercialMode: string;
  supplierName: string;
  stockPolicy: 'not_stocked_prelaunch';
  checkoutEnabled: false;
  authorizationStatus: string;
  launchGate: string;
  blockers: string[];
  rationale: string;
  liveDropRank: number;
  pdpPath: string;
  dropRole: string;
  pagePitch: string;
  marketResearch: {
    checkedAt: string;
    pricingRule: string;
    pricingPublishStatus: 'internal_benchmark_not_public_offer';
    benchmark: MarketBenchmark;
    targetPriceInclVat: number;
    targetVsCompetitorDelta: number;
    sampleEan13: string;
    sampleEanStatus: 'internal_sample_not_for_feed';
    eanPublicationGate: string;
    uncertainty: string;
  };
};

type LiveDropPayload = {
  generatedAt: string;
  title: string;
  decision: string;
  checkoutEnabled: false;
  count: number;
  products: LiveDropProduct[];
};

export const liveDrop = liveDropRaw as LiveDropPayload;
export const liveDropProducts = [...liveDrop.products].sort((a, b) => a.liveDropRank - b.liveDropRank);
export const liveDropCategoryOptions = [
  { key: 'alles', label: 'Alles', count: liveDropProducts.length },
  ...Object.entries(
    liveDropProducts.reduce<Record<string, number>>((counts, product) => {
      counts[product.category] = (counts[product.category] ?? 0) + 1;
      return counts;
    }, {})
  )
    .sort((a, b) => categoryLabel(a[0]).localeCompare(categoryLabel(b[0])))
    .map(([key, count]) => ({ key, label: categoryLabel(key), count }))
];

export const collectionCategories = liveDropCategoryOptions.filter((option) => option.key !== 'alles');

export const levelOptions = [
  { key: 'alles', label: 'Alle niveaus' },
  { key: 'starter', label: 'Starter' },
  { key: 'recreatief', label: 'Recreatief' },
  { key: 'intermediate', label: 'Intermediate' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'club', label: 'Club' }
];

export const intentOptions = [
  { key: 'alles', label: 'Alle doelen' },
  { key: 'indoor', label: 'Indoor' },
  { key: 'outdoor', label: 'Outdoor' },
  { key: 'club', label: 'Club/school' },
  { key: 'budget', label: 'Budget' },
  { key: 'premium', label: 'Premium' }
];

export function formatEuro(value: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(value);
}

export function findLiveDropProduct(pathname: string): LiveDropProduct | null {
  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;
  return liveDropProducts.find((product) => product.pdpPath === normalized) ?? null;
}

export function collectionPath(category: string): string {
  return `/collecties/${category}/`;
}

export function findCollectionCategory(pathname: string): string | null {
  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;
  const match = normalized.match(/^\/collecties\/([^/]+)\/$/);
  if (!match) return null;
  return collectionCategories.some((category) => category.key === match[1]) ? match[1] : null;
}

export function productMatchesIntent(product: LiveDropProduct, intent: string): boolean {
  if (intent === 'alles') return true;
  const haystack = `${product.name} ${product.category} ${product.audience} ${product.dropRole} ${product.pagePitch}`.toLowerCase();
  if (intent === 'premium') return product.priority !== 'A' || /premium|joola|head|merk/i.test(haystack);
  return haystack.includes(intent);
}

export function categoryLabel(category: string): string {
  return {
    startersets: 'Startersets',
    paddles: 'Paddles',
    ballen: 'Ballen',
    'netten-en-belijning': 'Netten & belijning',
    'grips-accessoires': 'Grips & accessoires',
    'tassen-accessoires': 'Tassen & accessoires',
    training: 'Training',
    'schoenen-en-bescherming': 'Schoenen & bescherming',
    clubpakketten: 'Clubpakketten'
  }[category] ?? category;
}

export function matchTypeLabel(matchType: BenchmarkMatchType): string {
  return {
    exact_or_close: 'Gebaseerd op marktprijscontrole',
    brand_family: 'Gebaseerd op merkfamilie-prijsrichting',
    category_proxy: 'Prijsindicatie op categorie',
    category_proxy_prorated: 'Omgerekende prijsindicatie',
    category_proxy_per_ball: 'Prijsindicatie per bal'
  }[matchType];
}

export function matchConfidenceLabel(matchType: BenchmarkMatchType): string {
  if (matchType === 'exact_or_close') return 'Duidelijke prijsbasis';
  if (matchType === 'brand_family') return 'Goede prijsrichting';
  return 'Indicatieve prijs';
}

export function commercialModeLabel(mode: string): string {
  return {
    lead_capture: 'Beschikbaarheidsupdate',
    quote_request: 'Offerte op aanvraag',
    supplier_verification: 'Binnenkort beschikbaar',
    affiliate_first: 'Adviesroute'
  }[mode] ?? mode.replace(/_/g, ' ');
}

export function productFitBullets(product: LiveDropProduct): string[] {
  const categoryFit = {
    startersets: 'Voor spelers die compleet willen starten zonder losse onderdelen uit te zoeken.',
    paddles: 'Voor spelers die een duidelijke stap maken van instapmateriaal naar gerichter gevoel.',
    ballen: 'Voor herhaalaankoop, clubtraining en het juiste indoor/outdoor verschil.',
    'netten-en-belijning': 'Voor sporthallen, opritten, clubs en scholen die snel een veld willen opzetten.',
    'grips-accessoires': 'Voor lage-drempel add-ons met comfort, grip en onderhoud als reden om terug te komen.',
    'tassen-accessoires': 'Voor bundels rond paddles, ballen en dagelijkse training.',
    training: 'Voor trainers en clubs die clinics en lessen serieuzer willen organiseren.',
    'schoenen-en-bescherming': 'Voor spelers die extra bescherming of goede court-schoenen willen kiezen.',
    clubpakketten: 'Voor clubs, scholen en bedrijven waar aantallen, locatie en planning eerst samen worden afgestemd.'
  }[product.category] ?? product.pagePitch;

  return [
    `Doelgroep: ${product.audience}.`,
    categoryFit,
    `Volgende stap: ${commercialModeLabel(product.commercialMode).toLowerCase()}.`
  ];
}

export function readinessBadges(product: LiveDropProduct): string[] {
  const badges = ['Binnenkort beschikbaar', 'Prijsupdate mogelijk', 'Definitieve info volgt'];
  if (product.commercialMode === 'quote_request') badges.push('Offerte op aanvraag');
  if (product.commercialMode === 'supplier_verification') badges.push('Merkproduct in voorbereiding');
  if (product.category === 'schoenen-en-bescherming') badges.push('Pasvorm en retourinfo volgen');
  return badges;
}

export function getRelatedProducts(product: LiveDropProduct, limit = 4): LiveDropProduct[] {
  const sameCategory = liveDropProducts.filter((candidate) => candidate.sku !== product.sku && candidate.category === product.category);
  const otherAddOns = liveDropProducts.filter((candidate) => candidate.sku !== product.sku && candidate.category !== product.category);
  return [...sameCategory, ...otherAddOns].slice(0, limit);
}

export function recommendProductsFromQuiz(playContext: string, buyerType: string, experience: string): LiveDropProduct[] {
  const scored = liveDropProducts.map((product) => {
    const text = `${product.name} ${product.category} ${product.audience} ${product.level} ${product.dropRole}`.toLowerCase();
    let score = 0;
    if (playContext !== 'any' && text.includes(playContext)) score += 3;
    if (buyerType === 'club' && (product.category === 'clubpakketten' || text.includes('club') || text.includes('school'))) score += 4;
    if (buyerType === 'consumer' && ['startersets', 'paddles', 'ballen'].includes(product.category)) score += 2;
    if (experience !== 'any' && product.level === experience) score += 3;
    if (experience === 'starter' && product.category === 'startersets') score += 3;
    if (product.category === 'ballen' || product.category === 'grips-accessoires') score += 1;
    return { product, score };
  });

  return scored
    .sort((a, b) => b.score - a.score || a.product.liveDropRank - b.product.liveDropRank)
    .slice(0, 4)
    .map((item) => item.product);
}
